import styled from "styled-components";
import { borderStyle, borderWidth } from "../../../design-system/tokens/borders";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { borderRadius as uiBorderRadius, fontFamilies, fontSizes, fontWeights, lineHeights, rawColors as uiRawColors, spacing as uiSpacing, typographyMixin as uiTypographyMixin, borderRadius, rawColors } from "@magicbi/ui-library";
import type { ChoiceCardIconTone } from "./ChoiceCard.types";

/* Selected: a 2px accent border and the page tint (F04 #FBFAFF) (the design's card grows by that 1px too). */
export const Card = styled.button<{ $selected: boolean; $soon?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing[1.5]};
  box-sizing: border-box;
  width: 100%;
  min-height: 7rem;
  /* Design: radius 14, padding 14. */
  padding: 0.875rem;
  border: ${({ $selected }) => ($selected ? borderWidth[2] : borderWidth[1])} ${borderStyle.solid}
    ${({ $selected }) => ($selected ? rawColors.primary[500] : rawColors.stroke[200])};
  border-radius: ${borderRadius[14]};
  background: ${({ $selected }) => ($selected ? rawColors.structural.themeLight : rawColors.structural.white)};
  color: ${rawColors.text.highEmphasis};
  text-align: left;
  cursor: ${({ $soon }) => ($soon ? "not-allowed" : "pointer")};

  &:hover {
    border-color: ${({ $selected }) => ($selected ? rawColors.primary[500] : rawColors.primary[300])};
  }

  &:focus-visible {
    outline: ${borderWidth[2]} ${borderStyle.solid} ${rawColors.primary[300]};
    outline-offset: 2px;
  }
`;

export const Badge = styled.span<{ $tone: ChoiceCardIconTone }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${spacing[8]};
  height: ${spacing[8]};
  /* Design: radius 10. */
  border-radius: ${borderRadius[10]};
  background: ${({ $tone }) =>
    $tone === "accent" ? rawColors.primary[100] : $tone === "info" ? rawColors.accent.cross.bg : rawColors.grey[300]};
  color: ${({ $tone }) =>
    $tone === "accent" ? rawColors.primary[500] : $tone === "info" ? rawColors.secondary[700] : rawColors.text.mediumEmphasis};
`;

export const Title = styled.b`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1.5]};
  /* Design: 14/700. */
  ${typographyMixin("body", 1)}
`;

export const Description = styled.span`
  color: ${rawColors.text.mediumEmphasis};
  ${typographyMixin("caption", 4)}
`;

/* `multiple`: a label wrapping a corner checkbox (the data app outputs picker). */

export const CheckCard = styled.label<{ $checked: boolean; $disabled: boolean; $tint?: "accent" | "page" }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${uiSpacing[2.5]};
  min-width: 0;
  padding: ${({ $checked }) => ($checked ? uiSpacing[4] : "calc(" + uiSpacing[4] + " + 1px)")};
  border: ${({ $checked }) => ($checked ? "2px" : "1px")} solid
    ${({ theme, $checked }) => ($checked ? theme.colors.interactive.primary : theme.colors.border.stroke)};
  border-radius: ${uiBorderRadius[20]};
  background: ${({ theme, $checked, $tint }) => ($checked ? ($tint === "page" ? uiRawColors.structural.themeLight : uiRawColors.primary[50]) : theme.colors.surface.primary)};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  &:focus-within {
    outline: 0.125rem solid ${({ theme }) => theme.colors.interactive.primary};
    outline-offset: 0.125rem;
  }
`;

export const CheckBox = styled.input`
  position: absolute;
  top: 0.875rem;
  right: 0.875rem;
  width: 1.125rem;
  height: 1.125rem;
  margin: 0;
  accent-color: ${({ theme }) => theme.colors.interactive.primary};
  cursor: inherit;
  /* The card draws the one focus ring. */
  &:focus-visible {
    outline: none;
  }
`;

export const CheckIconTile = styled.span<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${uiBorderRadius.xl};
  background: ${({ theme, $checked }) => ($checked ? uiRawColors.primary[100] : theme.colors.surface.secondary)};
  color: ${({ theme, $checked }) => ($checked ? theme.colors.interactive.primary : theme.colors.text.secondary)};
`;

export const CheckLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${uiSpacing[1.5]};
  font-family: ${fontFamilies.sans};
  font-size: ${fontSizes['15']};
  font-weight: ${fontWeights.bold};
  line-height: ${lineHeights['22']};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const CheckDescription = styled.span`
  ${uiTypographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;
